import { createContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { adminClient } from '@/api/adminClient';
import type { AdminUser, Organization } from '@/types';

interface AuthContextType {
  user: AdminUser | null;
  organization: Organization | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (organizationName: string, ownerEmail: string, ownerPassword: string, ownerDisplayName?: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Auto-refresh token before expiry
  useEffect(() => {
    const setupTokenRefresh = () => {
      const tokens = adminClient.getStoredTokens();
      if (!tokens) return;

      const expiresIn = tokens.expiresIn || 3600;
      const refreshTime = (expiresIn - 300) * 1000; // Refresh 5 minutes before expiry

      const timeoutId = setTimeout(async () => {
        try {
          await adminClient.refreshToken();
          setupTokenRefresh(); // Schedule next refresh
        } catch (err) {
          console.error('Token refresh failed:', err);
          setUser(null);
          setOrganization(null);
        }
      }, refreshTime);

      return () => clearTimeout(timeoutId);
    };

    const cleanup = setupTokenRefresh();
    return cleanup;
  }, [user]);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      if (adminClient.isAuthenticated()) {
        const data = await adminClient.getCurrentUser();
        setUser(data.user);
        setOrganization(data.organization);
      } else {
        setUser(null);
        setOrganization(null);
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      setUser(null);
      setOrganization(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminClient.login(email, password);
      setUser(data.user);
      setOrganization(data.organization);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Login failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const signup = useCallback(async (
    organizationName: string,
    ownerEmail: string,
    ownerPassword: string,
    ownerDisplayName?: string
  ) => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminClient.signup(organizationName, ownerEmail, ownerPassword, ownerDisplayName);
      setUser(data.user);
      setOrganization(data.organization);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Signup failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await adminClient.logout();
      setUser(null);
      setOrganization(null);
    } catch (err) {
      console.error('Logout failed:', err);
    }
  }, []);

  const value: AuthContextType = {
    user,
    organization,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    signup,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
